import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useNotification } from '../components/Notification';
import { useLanguage } from '../context/LanguageContext';
import apiClient from '../api/client';
import './Contact.css';

// value sent to API (fixed); label shown via t(labelKey)
const CONTACT_SUBJECTS = [
  { value: '', labelKey: 'selectSubject' },
  { value: 'Susisiekti su pardavimais', labelKey: 'contactSales' },
  { value: 'Susisiekti su aptarnavimu', labelKey: 'contactService' },
  { value: 'Susisiekti dėl kokybės', labelKey: 'contactQuality' },
  { value: 'Susisiekti dėl garantijos', labelKey: 'contactWarranty' },
];

const validSubjects = CONTACT_SUBJECTS.filter((s) => s.value).map((s) => s.value);

function Contact() {
  const notification = useNotification();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [sending, setSending] = useState(false);
  const formLoadedAt = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website: '', // honeypot – tuščias; jei užpildytas, laikoma spamu
  });

  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('token');
  const { data: profile } = useQuery({
    queryKey: ['user-profile'],
    queryFn: async () => {
      const res = await apiClient.get('/user/profile');
      return res.data;
    },
    enabled: hasToken,
  });

  useEffect(() => {
    if (formLoadedAt.current === null) {
      formLoadedAt.current = Date.now();
    }
  }, []);

  useEffect(() => {
    const subjectFromUrl = searchParams.get('subject');
    if (subjectFromUrl) {
      const decoded = decodeURIComponent(subjectFromUrl.replace(/\+/g, ' '));
      if (validSubjects.includes(decoded)) {
        setFormData((prev) => ({ ...prev, subject: decoded }));
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (!profile) return;
    const displayName = [profile.first_name, profile.last_name].filter(Boolean).join(' ').trim();
    setFormData((prev) => ({
      ...prev,
      name: prev.name === '' ? (displayName || '') : prev.name,
      email: prev.email === '' ? (profile.email || '') : prev.email,
    }));
  }, [profile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const elapsed = formLoadedAt.current ? Date.now() - formLoadedAt.current : 0;
    const payload = {
      name: formData.name,
      email: formData.email,
      subject: formData.subject,
      message: formData.message,
      _website: formData.website,
      _formTime: elapsed,
    };
    setSending(true);
    try {
      await apiClient.post('/contact', payload);
      notification.success(t('contactSuccess'));
      setFormData((prev) => ({ ...prev, subject: '', message: '', website: '' }));
    } catch (err) {
      const msg = err.response?.data?.error || t('contactError');
      notification.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="contact-page">
      <div className="container contact-container">
        <div className="contact-content">
          <h1>{t('contactTitle')}</h1>
          <p className="contact-intro">
            {t('contactIntro')}
          </p>

          <form onSubmit={handleSubmit} className="contact-form">
            <div className="contact-form-group">
              <label htmlFor="contact-name">{t('contactNameLabel')}</label>
              <input
                id="contact-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="contact-input"
                required
                placeholder={t('contactNamePlaceholder')}
                autoComplete="name"
              />
            </div>
            <div className="contact-form-group">
              <label htmlFor="contact-email">{t('email')} *</label>
              <input
                id="contact-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="contact-input"
                required
                placeholder="email@example.com"
                autoComplete="email"
              />
            </div>
            <div className="contact-form-group">
              <label htmlFor="contact-subject">{t('subject')}</label>
              <select
                id="contact-subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="contact-input contact-select"
              >
                {CONTACT_SUBJECTS.map((opt) => (
                  <option key={opt.value || 'empty'} value={opt.value}>
                    {t(opt.labelKey)}
                  </option>
                ))}
              </select>
            </div>
            <div className="contact-form-group">
              <label htmlFor="contact-message">{t('message')} *</label>
              <textarea
                id="contact-message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                className="contact-input contact-textarea"
                required
                rows={6}
                placeholder={t('contactMessagePlaceholder')}
              />
            </div>
            {/* Honeypot: nematomas laukas; botai dažnai jį užpildo – tada siuntimas blokuojamas */}
            <div className="contact-honeypot" aria-hidden="true">
              <label htmlFor="contact-website">Nepildykite</label>
              <input
                id="contact-website"
                type="text"
                name="website"
                value={formData.website}
                onChange={handleChange}
                className="contact-input"
                tabIndex={-1}
                autoComplete="off"
              />
            </div>
            <div className="contact-form-actions">
              <button type="submit" className="contact-submit" disabled={sending}>
                {sending ? t('sending') : t('send')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Contact;
